import { Coordinates, Level } from '@wemap/geo';
import { Graph } from '@wemap/routers';
import type { Map } from 'maplibre-gl';
import { getLevelProperty } from '@wemap/map';
import type { WemapMap } from '@wemap/map';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from './geoJsonSource.js';

const PROJECTION_LINES_SOURCE = 'projection-lines';
const PINPOINT_CIRCLES_SOURCE = 'pinpoint-circles';

export default class PinpointsHandler {
    _pinpoints: any[] = [];
    emmid: number;
    private readonly map: Map;
    graph: Graph | null = null;
    projectionFeatures: any[] = [];
    pinpointFeatures: any[] = [];
    
    layersID = [PROJECTION_LINES_SOURCE, PINPOINT_CIRCLES_SOURCE];

    constructor(wemapMap: WemapMap, emmid: number) {
        this.map = wemapMap.maplibre;
        this.emmid = emmid;

        wemapMap.addSource(PROJECTION_LINES_SOURCE, {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
        });

        wemapMap.addLayer({
            id: PROJECTION_LINES_SOURCE,
            type: 'line',
            source: PROJECTION_LINES_SOURCE,
            paint: {
                'line-color': '#8B5CF6',
                'line-dasharray': [2, 2],
                'line-width': 2
            }
        }, { indoor: true });

        wemapMap.addSource(PINPOINT_CIRCLES_SOURCE, {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
        });

        wemapMap.addLayer({
            id: PINPOINT_CIRCLES_SOURCE,
            type: 'circle',
            source: PINPOINT_CIRCLES_SOURCE,
            paint: {
                'circle-radius': 4,
                'circle-color': [
                    'case',
                    ['!=', ['get', 'level'], null], '#4C1D95',
                    '#C084FC'
                ],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#FFFFFF'
            }
        }, { indoor: true });
    }

    get pinpoints() {
        return this._pinpoints;
    }

    set pinpoints(pinpoints) {
        this._pinpoints = pinpoints;
    }

    setGraph(graph: Graph) {
        this.graph = graph;
    }

    fetchAllPinpoints = async () => {
        const totalResults = [];

        let count = 0;
        let offset = 0;
        const QUERY_ALL_SLICE = 1000;

        do {
            const response = await fetch(`https://api.getwemap.com/v3.0/pinpoints/search?livemap=${this.emmid}&limit=${QUERY_ALL_SLICE}&offset=${offset}`).then((res) => res.json());
            count = count || response.count;

            totalResults.push(...response.results);

            offset += QUERY_ALL_SLICE;
        } while (totalResults.length < count);

        this.pinpoints = totalResults;

        return totalResults;
    };

    checkPinpointsProjection = async () => {
        if (!this.graph || !this.pinpoints.length || this.projectionFeatures.length) {
            return;
        }

        this.projectionFeatures = [];
        this.pinpointFeatures = [];

        for (const pinpoint of this.pinpoints) {
            const pinpointCoordinates = new Coordinates(pinpoint.latitude, pinpoint.longitude, null, pinpoint.level);
            const projection = this.graph.getProjection(pinpointCoordinates, {useMultiLevelSegments: false});
            
            this.pinpointFeatures.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [pinpoint.longitude, pinpoint.latitude]
                },
                properties: {
                    ...(pinpoint.level !== null ? {
                        level: pinpoint.level,
                        ...getLevelProperty(pinpoint.level)
                    } : {})
                }
            });
            
            if (!projection) {
                continue;
            }
            
            const projectionCoordinates = projection.coords;
            
            this.projectionFeatures.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [pinpoint.longitude, pinpoint.latitude],
                        [projectionCoordinates.lng, projectionCoordinates.lat]
                    ]
                },
                properties: {
                    ...(pinpoint.level !== null ? getLevelProperty(pinpoint.level) : {})
                }
            });
        }

        this._setProjectionData();
    }

    private _setProjectionData() {
        if (!this.map.style) {
            return;
        }

        setGeoJsonSourceData(this.map, PROJECTION_LINES_SOURCE, {
            type: 'FeatureCollection',
            features: this.projectionFeatures
        });

        setGeoJsonSourceData(this.map, PINPOINT_CIRCLES_SOURCE, {
            type: 'FeatureCollection',
            features: this.pinpointFeatures
        });
    }

    hideProjectionFeatures = () => {
        setGeoJsonSourceData(this.map, PROJECTION_LINES_SOURCE, EMPTY_FEATURE_COLLECTION);
        setGeoJsonSourceData(this.map, PINPOINT_CIRCLES_SOURCE, EMPTY_FEATURE_COLLECTION);
    }

    showProjectionFeatures = () => {
        if (!this.projectionFeatures.length) {
            this.checkPinpointsProjection();
            return;
        }

        this._setProjectionData();
    }
}
